/*!
 *
 *  Web Starter Kit
 *  Copyright 2015 Google Inc. All rights reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *    https://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License
 *
 */
/* eslint-env browser */
/* global componentHandler */
(function(componentHandler) {
  'use strict';

  // Check to make sure service workers are supported in the current browser,
  // and that the current page is accessed from a secure origin. Using a
  // service worker from an insecure origin will trigger JS console errors. See
  // http://www.chromium.org/Home/chromium-security/prefer-secure-origins-for-powerful-new-features
  var isLocalhost = Boolean(window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
  );

  if ('serviceWorker' in navigator &&
    (window.location.protocol === 'https:' || isLocalhost)) {
    navigator.serviceWorker.register('service-worker.js')
      .then(function(registration) {
        // Check to see if there's an updated version of service-worker.js with
        // new files to cache:
        // https://slightlyoff.github.io/ServiceWorker/spec/service_worker/index.html#service-worker-registration-update-method
        if (typeof registration.update === 'function') {
          registration.update();
        }

        // updatefound is fired if service-worker.js changes.
        registration.onupdatefound = function() {
          // updatefound is also fired the very first time the SW is installed,
          // and there's no need to prompt for a reload at that point.
          // So check here to see if the page is already controlled,
          // i.e. whether there's an existing service worker.
          if (navigator.serviceWorker.controller) {
            // The updatefound event implies that registration.installing is set:
            // https://slightlyoff.github.io/ServiceWorker/spec/service_worker/index.html#service-worker-container-updatefound-event
            var installingWorker = registration.installing;

            installingWorker.onstatechange = function() {
              switch (installingWorker.state) {
                case 'installed':
                  // At this point, the old content will have been purged and the
                  // fresh content will have been added to the cache.
                  // It's the perfect time to display a "New content is
                  // available; please refresh." message in the page's interface.
                  break;

                case 'redundant':
                  throw new Error('The installing ' +
                    'service worker became redundant.');

                default:
                // Ignore
              }
            };
          }
        };
      })
      .catch(function(e) {
        console.error('Error during service worker registration:', e);
      });
  }

  // Your custom JavaScript goes here

  // region Local variables
  var _attributeRepository;
  var _skillRepository;
  var _meritRepository;
  // endregion

  // region Classes
  /**
   * Represents a Character.
   *
   * @param {Attribute[]} attributes Array of Attribute objects.
   * @param {Skill[]} skills Array of Skill objects.
   * @param {Merit[]} merits Array of Merit objects.
   * @constructor
   */
  function Character(attributes, skills, merits) {
    this.attributes = attributes ? attributes : [];
    this.skills = skills ? skills : [];
    this.merits = merits ? merits : [];
  }

  /**
   * Represents an Attribute.
   *
   * @param {string} name Name of the Attribute.
   * @param {string} category Category of the Attribute.
   * @constructor
   */
  function Attribute(name, category) {
    this.name = name;
    this.id = toLowerCamelCase(name);
    this.category = category;
    this.value = 0;
  }

  /**
   * Represents a Skill.
   *
   * @param {string} name Name of the Skill.
   * @param {string} category Category of the Skill.
   * @constructor
   */
  function Skill(name, category) {
    this.name = name;
    this.id = toLowerCamelCase(name);
    this.specialtyId = this.id + 'Specialty';
    this.category = category;
    this.value = 0;
    this.hasSpecialty = false;
  }

  /**
   * Represents a Merit.
   *
   * @param {String} name Name of the Merit.
   * @param {string} category Category of Merit.
   * @param {[Prerequisite[]]} prerequisiteGroups Groups of prerequisites. A Merit may have multiple groups of criteria
   * where only a single group has to be met for a Character to be eligible for the Merit.
   * @param {boolean} isTrained Indicates if the Merit has been trained.
   * @constructor
   */
  function Merit(name, category, prerequisiteGroups, isTrained) {
    this.name = name;
    this.id = toLowerCamelCase(name);
    this.category = category;
    this.prerequisiteGroups = prerequisiteGroups ? prerequisiteGroups : [[]];
    this.isTrained = isTrained ? isTrained : false;
  }

  /**
   * Superclass for all prerequisites. A prerequisite is used when determining if a Character is eligible for a Merit.
   *
   * @param {string} prerequisiteType The type of Prerequisite.
   * @param {string} elementName The name of the element the Prerequisite keys off of.
   * @param {int|boolean} elementValue The minimum value the element for this prerequisite needs for this prerequisite's criteria to
   * be met.
   * @constructor
   */
  function Prerequisite(prerequisiteType, elementName, elementValue) {
    this.type = prerequisiteType;
    this.elementId = toLowerCamelCase(elementName);
    this.elementValue = elementValue;
  }

  /**
   * Represents a prerequisite that uses an Attribute. Inherits from Prerequisite.
   *
   * @param {string} attributeName Name of the Attribute this prerequisite uses.
   * @param {int} value The minimum value the Attribute for this prerequisite needs for this prerequisite's criteria to
   * be met.
   * @constructor
   */
  function AttributePrerequisite(attributeName, value) {
    Prerequisite.call(this, 'attribute', attributeName, value);
  }

  /**
   * Represents a prerequisite that uses a Skill. Inherits from Prerequisite.
   *
   * @param {string} skillName Name of the Skill this prerequisite uses.
   * @param {int} value The minimum value the Skill for this prerequisite needs for this prerequisite's criteria to be
   * met.
   * @constructor
   */
  function SkillPrerequisite(skillName, value) {
    Prerequisite.call(this, 'skill', skillName, value);
  }

  /**
   * Represents a prerequisite that uses the hasSkillSpecialty property of a Skill. Inherits from Prerequisite.
   *
   * @param {string} skillName Name of the Skill this prerequisite uses.
   * @param {int} value The minimum value the Skill for this prerequisite needs for this prerequisite's criteria to be
   * met.
   * @constructor
   */
  function SkillSpecialtyPrerequisite(skillName, value) {
    if (!value) {
      value = 1;
    }

    Prerequisite.call(this, 'skill-specialty', skillName,
      value);
  }

  /**
   * Represents a prerequisite that uses the isTrained property of a Merit. Inherits from Prerequisite.
   *
   * @param {string} meritName Name of the Merit this prerequisite uses.
   * @param {boolean} isTrained Whether the merit needs to be trained in meet this prerequisite's criteria.
   * met.
   * @constructor
   */
  function MeritPrerequisite(meritName, isTrained) {
    if (!isTrained) {
      isTrained = false;
    }

    Prerequisite.call(this, 'merit', meritName, isTrained);
  }

  // region Setting prototypes of subclasses
  AttributePrerequisite.prototype = Object.create(Prerequisite.prototype);
  SkillPrerequisite.prototype = Object.create(Prerequisite.prototype);
  SkillSpecialtyPrerequisite.prototype = Object.create(Prerequisite.prototype);
  MeritPrerequisite.prototype = Object.create(Prerequisite.prototype);
  // endregion
  // endregion

  // region Functions
  // region Helpers
  /**
   * Given a string, returns a copy of it in camel case format with the first letter lower case.
   *
   * @param {String} str The string to transform.
   * @return {XML|string} Camel case copy of original string.
   */
  function toLowerCamelCase(str) {
    return str.replace(/[^A-Za-z0-9]/g, ' ').replace(/^\w|[A-Z]|\b\w|\s+/g,
      function(match, index) {
        if (Number(match) === 0 || match === '-' || match === '.') {
          return '';
        }
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
      });
  }

  /**
   * Returns a Character object built with current form values.
   *
   * @return {Character} The Character object.
   */
  function getCharacter() {
    for (var i = 0; i < _attributeRepository.length; i++) {
      var attributeElement = document.getElementById(_attributeRepository[i].id);

      if (attributeElement) {
        _attributeRepository[i].value = parseInt(attributeElement.value, 10);
      }
    }

    for (var j = 0; j < _skillRepository.length; j++) {
      var skillElement = document.getElementById(_skillRepository[j].id);
      var skillSpecialtyElement = document.getElementById(_skillRepository[j].specialtyId);

      if (skillElement) {
        _skillRepository[j].value = parseInt(skillElement.value, 10);
      }

      if (skillSpecialtyElement) {
        _skillRepository[j].hasSpecialty = skillSpecialtyElement.checked;
      }
    }

    for (var k = 0; k < _meritRepository.length; k++) {
      var meritElement = document.getElementById(_meritRepository[k].id);

      if (meritElement) {
        _meritRepository[k].isTrained = meritElement.checked;
      }
    }

    return new Character(_attributeRepository, _skillRepository, _meritRepository);
  }

  /**
   * Clears all Merit lists
   */
  function clearMeritLists() {
    clearMeritList(document.getElementById('meritsMental'));
    clearMeritList(document.getElementById('meritsPhysical'));
    clearMeritList(document.getElementById('meritsSocial'));
    clearMeritList(document.getElementById('meritsSupernatural'));
    clearMeritList(document.getElementById('meritsFighting'));
  }

  /**
   * Clears all Merits from a given Merit list.
   *
   * @param {Node} meritList The Merit list to clear.
   */
  function clearMeritList(meritList) {
    while (meritList.firstChild) {
      meritList.removeChild(meritList.firstChild);
    }
  }

  // endregion

  // region Initialization
  /**
   * Initializes the project for use.
   */
  function initialize() {
    attachListeners();
    initializeRepositories();
    displayAttributes();
    displaySkills();
    updateEligibleMeritList();
  }

  /**
   * Attaches listeners for the page.
   */
  function attachListeners() {
    var splatNavigation = document.getElementsByClassName('navigation-splat');

    for (var i = 0; i < splatNavigation.length; i++) {
      splatNavigation[i].addEventListener('click', setPageCss);
      splatNavigation[i].addEventListener('click', buildMeritRepository);
    }
  }

  // endregion

  // region Listener Events
  /**
   * Sets the page css based off the attribute 'data-css_href' in the element.
   */
  function setPageCss() {
    var pageStyle = document.getElementById('mdlPageStyle');
    var cssLink = this.getAttribute('data-css_href');
    pageStyle.setAttribute('href', cssLink);
  }

  /**
   * Builds the Merit repository to include only the Merits for the selected splat.
   */
  function buildMeritRepository() {
    var splat = this.textContent;
    _meritRepository = getCoreMerits();

    switch (splat) {
      case 'Mortal':
        Array.prototype.push.apply(_meritRepository, getMortalMerits());
        break;
      case 'Vampire':
        Array.prototype.push.apply(_meritRepository, getVampireMerits());
        break;
      case 'Werewolf':
        Array.prototype.push.apply(_meritRepository, getWerewolfMerits());
        break;
      case 'Mage':
        Array.prototype.push.apply(_meritRepository, getMageMerits());
        break;
      default:
        break;
    }

    updateEligibleMeritList();
  }

  /**
   * Updates the eligible merit list.
   */
  function updateEligibleMeritList() {
    var character = getCharacter();

    clearMeritLists();

    for (var i = 0; i < _meritRepository.length; i++) {
      if (characterHasMeritPrerequisites(character, _meritRepository[i])) {
        displayMerit(_meritRepository[i]);
      }
    }
  }

  // endregion

  // region Display Traits
  /**
   * Displays the Attributes in the appropriate section of the website.
   */
  function displayAttributes() {
    for (var i = 0; i < _attributeRepository.length; i++) {
      switch (_attributeRepository[i].category) {
        case 'mental':
          appendAttributeToElement(_attributeRepository[i], document.getElementById('attributesMental'));
          break;
        case 'physical':
          appendAttributeToElement(_attributeRepository[i], document.getElementById('attributesPhysical'));
          break;
        case 'social':
          appendAttributeToElement(_attributeRepository[i], document.getElementById('attributesSocial'));
          break;
        default:
          break;
      }
    }
  }

  /**
   * Appends Attribute to the given element.
   *
   * @param {Attribute} attribute The Attribute to append.
   * @param {Node} element The element to append to.
   */
  function appendAttributeToElement(attribute, element) {
    var listItem = document.createElement('li');

    var grid = document.createElement('div');
    grid.className = 'mdl-grid';

    var firstCell = document.createElement('label');
    firstCell.setAttribute('for', attribute.id);
    firstCell.className = 'mdl-cell mdl-cell--5-col-desktop mdl-cell--2-col-tablet mdl-cell--3-col-phone';
    firstCell.textContent = attribute.name;

    var secondCell = document.createElement('div');
    secondCell.className = 'mdl-cell mdl-cell--7-col-desktop mdl-cell--6-col-tablet mdl-cell--1-col-phone mdl-textfield mdl-js-textfield';

    var input = document.createElement('input');
    input.setAttribute('type', 'number');
    input.setAttribute('id', attribute.id);
    input.setAttribute('name', attribute.id);
    input.setAttribute('min', '0');
    input.setAttribute('max', '5');
    input.setAttribute('value', attribute.value.toString());
    input.className = 'mdl-textfield__input';
    input.addEventListener('change', updateEligibleMeritList);

    var textFieldLabel = document.createElement('label');
    textFieldLabel.setAttribute('for', attribute.id);
    textFieldLabel.className = 'mdl-textfield__label';
    textFieldLabel.textContent = 'Dots...';

    secondCell.appendChild(input);
    secondCell.appendChild(textFieldLabel);
    componentHandler.upgradeElement(secondCell);

    grid.appendChild(firstCell);
    grid.appendChild(secondCell);

    listItem.appendChild(grid);

    element.appendChild(listItem);
  }

  /**
   * Displays the Skills in the appropriate section of the website.
   */
  function displaySkills() {
    for (var i = 0; i < _skillRepository.length; i++) {
      switch (_skillRepository[i].category) {
        case 'mental':
          appendSkillToElement(_skillRepository[i], document.getElementById('skillsMental'));
          break;
        case 'physical':
          appendSkillToElement(_skillRepository[i], document.getElementById('skillsPhysical'));
          break;
        case 'social':
          appendSkillToElement(_skillRepository[i], document.getElementById('skillsSocial'));
          break;
        default:
          break;
      }
    }
  }

  /**
   * Appends Skill to the given element.
   *
   * @param {Skill} skill The Skill to append.
   * @param {Node} element The element to append to.
   */
  function appendSkillToElement(skill, element) {
    var listItem = document.createElement('li');

    var grid = document.createElement('div');
    grid.className = 'mdl-grid';

    var firstCell = document.createElement('label');
    firstCell.setAttribute('for', skill.specialtyId);
    firstCell.className = 'mdl-cell mdl-cell--3-col-desktop mdl-cell--1-col-tablet mdl-cell--1-col-phone mdl-switch mdl-js-switch';

    var specialtyInput = document.createElement('input');
    specialtyInput.setAttribute('type', 'checkbox');
    specialtyInput.setAttribute('id', skill.specialtyId);
    specialtyInput.setAttribute('name', skill.id);
    specialtyInput.setAttribute('title', 'Has specialty?');
    specialtyInput.className = 'mdl-switch__input';
    specialtyInput.addEventListener('change', updateEligibleMeritList);

    var switchLabel = document.createElement('span');
    switchLabel.className = 'mdl-switch__label';

    var secondCell = document.createElement('div');
    secondCell.className = 'mdl-cell mdl-cell--6-col-desktop mdl-cell--2-col-tablet mdl-cell--2-col-phone';

    var label = document.createElement('label');
    label.setAttribute('for', skill.id);
    label.textContent = skill.name;

    var thirdCell = document.createElement('div');
    thirdCell.className = 'mdl-cell mdl-cell--3-col-desktop mdl-cell--5-col-tablet mdl-cell--1-col-phone mdl-textfield mdl-js-textfield';

    var input = document.createElement('input');
    input.setAttribute('type', 'number');
    input.setAttribute('id', skill.id);
    input.setAttribute('name', skill.id);
    input.setAttribute('min', '0');
    input.setAttribute('max', '5');
    input.setAttribute('value', skill.value.toString());
    input.className = 'mdl-textfield__input';
    input.addEventListener('change', updateEligibleMeritList);

    var textFieldLabel = document.createElement('label');
    textFieldLabel.setAttribute('for', skill.id);
    textFieldLabel.className = 'mdl-textfield__label';
    textFieldLabel.textContent = 'Dots...';

    firstCell.appendChild(specialtyInput);
    firstCell.appendChild(switchLabel);
    componentHandler.upgradeElement(firstCell);

    secondCell.appendChild(label);

    thirdCell.appendChild(input);
    thirdCell.appendChild(textFieldLabel);
    componentHandler.upgradeElement(thirdCell);

    grid.appendChild(firstCell);
    grid.appendChild(secondCell);
    grid.appendChild(thirdCell);

    listItem.appendChild(grid);

    element.appendChild(listItem);
  }

  /**
   * Displays the Merit in the appropriate section of the website.
   *
   * @param {Merit} merit The Merit to display.
   */
  function displayMerit(merit) {
    switch (merit.category) {
      case 'mental':
        appendMeritToElement(merit, document.getElementById('meritsMental'));
        break;
      case 'physical':
        appendMeritToElement(merit, document.getElementById('meritsPhysical'));
        break;
      case 'social':
        appendMeritToElement(merit, document.getElementById('meritsSocial'));
        break;
      case 'supernatural':
        appendMeritToElement(merit,
          document.getElementById('meritsSupernatural'));
        break;
      case 'fighting':
        appendMeritToElement(merit, document.getElementById('meritsFighting'));
        break;
      default:
        break;
    }
  }

  /**
   * Appends Merit to the given element.
   *
   * @param {Merit} merit The Merit to append.
   * @param {Node} element The element to append to.
   */
  function appendMeritToElement(merit, element) {
    var listItem = document.createElement('li');

    var grid = document.createElement('div');
    grid.className = 'mdl-grid';

    var firstCell = document.createElement('label');
    firstCell.setAttribute('for', merit.id);
    firstCell.className = 'mdl-cell mdl-cell--3-col-desktop mdl-cell--1-col-tablet mdl-cell--1-col-phone mdl-switch mdl-js-switch';

    var input = document.createElement('input');
    input.setAttribute('type', 'checkbox');
    input.setAttribute('id', merit.id);
    input.setAttribute('name', merit.id);
    input.setAttribute('title', 'Is trained?');
    input.className = 'mdl-switch__input';
    input.addEventListener('change', updateEligibleMeritList);

    if (merit.isTrained) {
      input.setAttribute('checked', 'checked');
    }

    var switchLabel = document.createElement('span');
    switchLabel.className = 'mdl-switch__label';

    var secondCell = document.createElement('div');
    secondCell.className = 'mdl-cell mdl-cell--9-col-desktop mdl-cell--7-col-tablet mdl-cell--3-col-phone';

    var label = document.createElement('label');
    label.setAttribute('for', merit.id);
    label.textContent = merit.name;

    firstCell.appendChild(input);
    firstCell.appendChild(switchLabel);
    componentHandler.upgradeElement(firstCell);

    secondCell.appendChild(label);

    grid.appendChild(firstCell);
    grid.appendChild(secondCell);

    listItem.appendChild(grid);

    element.appendChild(listItem);
  }

  // endregion

  // region Prerequisites
  /**
   * Determines if a Character meets the prerequisites for a Merit.
   *
   * @param {Character} character The Character object to test.
   * @param {Merit} merit The Merit object to test against.
   * @return {boolean} Returns true if Character meets Merit prerequisites.
   */
  function characterHasMeritPrerequisites(character, merit) {
    // If a merit doesn't have any prerequisites, then character is eligible for merit.
    if (merit.prerequisiteGroups === undefined ||
      merit.prerequisiteGroups.length === 0) {
      return true;
    }

    // If any prerequisiteGroup meets all of that prerequisiteGroup's criteria, then character is eligible for merit.
    for (var i = 0; i < merit.prerequisiteGroups.length; i++) {
      var prerequisites = merit.prerequisiteGroups[i];
      var hasMetGroupPrerequisites = true;

      // Test each prerequisite to see if criteria is met. prerequisiteType is used to determine the method of testing.
      for (var n = 0; n < prerequisites.length; n++) {
        var prerequisite = prerequisites[n];

        switch (prerequisite.type) {
          case 'attribute':
            if (!meetsAttributePrerequisite(character, prerequisite)) {
              hasMetGroupPrerequisites = false;
            }
            break;
          case 'skill':
            if (!meetsSkillPrerequisite(character, prerequisite)) {
              hasMetGroupPrerequisites = false;
            }
            break;
          case 'skill-specialty':
            if (!meetsSkillSpecialtyPrerequisite(character, prerequisite)) {
              hasMetGroupPrerequisites = false;
            }
            break;
          case 'merit':
            if (!meetsMeritPrerequisite(character, prerequisite)) {
              hasMetGroupPrerequisites = false;
            }
            break;
          default:
            hasMetGroupPrerequisites = false;
        }
      }

      if (hasMetGroupPrerequisites === true) {
        return true;
      }
    }

    return false;
  }

  /**
   * Criteria to determine if an element's id property matches the prerequisite's elementId property. Used to get
   * the correct element when attempting to test criteria for a Prerequisite;
   *
   * @param {Attribute|Skill|Merit} element The element to test.
   * @return {boolean} Returns true if the element's name matches.
   */
  function findByElementId(element) {
    return element.id === this.elementId;
  }

  /**
   * Criteria used to determine if a Skill has a qualified skill specialty.
   *
   * @param {Skill} element The Skill to test.
   * @return {boolean} Returns true if the Skill being tested has a qualified skill specialty.
   */
  function findByAnySkillSpecialty(element) {
    return element.hasSpecialty;
  }

  /**
   * Determines if a Character meets the prerequisite for an Attribute.
   *
   * @param {Character} character The Character to test.
   * @param {AttributePrerequisite} prerequisite The prerequisite to test against.
   * @return {boolean} Returns true if the Character meets the prerequisites.
   */
  function meetsAttributePrerequisite(character, prerequisite) {
    var meetsPrerequisite = true;
    var attribute = character.attributes.find(findByElementId, prerequisite);

    if (attribute === undefined ||
      attribute.value < prerequisite.elementValue) {
      meetsPrerequisite = false;
    }

    return meetsPrerequisite;
  }

  /**
   * Determines if a Character meets the prerequisite for a Skill.
   *
   * @param {Character} character The Character to test.
   * @param {SkillPrerequisite} prerequisite The prerequisite to test against.
   * @return {boolean} Returns true if the Character meets the prerequisites.
   */
  function meetsSkillPrerequisite(character, prerequisite) {
    var meetsPrerequisite = true;
    var skill = character.skills.find(findByElementId, prerequisite);

    if (skill === undefined || skill.value < prerequisite.elementValue) {
      meetsPrerequisite = false;
    }

    return meetsPrerequisite;
  }

  /**
   * Determines if a Character meets the prerequisite for a skill specialty.
   *
   * @param {Character} character The Character to test.
   * @param {SkillSpecialtyPrerequisite} prerequisite The prerequisite to test against.
   * @return {boolean} Returns true if the Character meets the prerequisites.
   */
  function meetsSkillSpecialtyPrerequisite(character, prerequisite) {
    var skill;
    var meetsPrerequisite = true;

    if (prerequisite.elementId === 'any') {
      skill = character.skills.find(findByAnySkillSpecialty);

      if (skill === undefined || skill.value < prerequisite.elementValue) {
        meetsPrerequisite = false;
      }
    } else {
      skill = character.skills.find(findByElementId, prerequisite);

      if (skill === undefined || !skill.hasSpecialty ||
        skill.value < prerequisite.elementValue) {
        meetsPrerequisite = false;
      }
    }

    return meetsPrerequisite;
  }

  /**
   * Determines if a Character meets the prerequisite for a Merit.
   *
   * @param {Character} character The Character to test.
   * @param {MeritPrerequisite} prerequisite The prerequisite to test against.
   * @return {boolean} Returns true if the Character meets the prerequisites.
   */
  function meetsMeritPrerequisite(character, prerequisite) {
    var meetsPrerequisite = true;

    var merit = character.merits.find(findByElementId, prerequisite);

    if (merit === undefined || merit.isTrained !== prerequisite.elementValue) {
      meetsPrerequisite = false;
    }

    return meetsPrerequisite;
  }

  // endregion

  // region Repositories
  /**
   * Initializes repositories for use.
   */
  function initializeRepositories() {
    if (!_attributeRepository) {
      _attributeRepository = getAttributeRepository();
    }

    if (!_skillRepository) {
      _skillRepository = getSkillRepository();
    }

    if (!_meritRepository) {
      _meritRepository = getCoreMerits();
      Array.prototype.push.apply(_meritRepository, getMortalMerits());
    }
  }

  /**
   * Returns an array of Attributes.
   *
   * @return {Attribute[]} The array of Attributes.
   */
  function getAttributeRepository() {
    return [
      new Attribute('Intelligence', 'mental'),
      new Attribute('Wits', 'mental'),
      new Attribute('Resolve', 'mental'),
      new Attribute('Strength', 'physical'),
      new Attribute('Dexterity', 'physical'),
      new Attribute('Stamina', 'physical'),
      new Attribute('Presence', 'social'),
      new Attribute('Manipulation', 'social'),
      new Attribute('Composure', 'social')
    ];
  }

  /**
   * Returns an array of Skills.
   *
   * @return {Skill[]} The array of Skills.
   */
  function getSkillRepository() {
    return [
      new Skill('Academics', 'mental'),
      new Skill('Computer', 'mental'),
      new Skill('Crafts', 'mental'),
      new Skill('Investigation', 'mental'),
      new Skill('Medicine', 'mental'),
      new Skill('Occult', 'mental'),
      new Skill('Politics', 'mental'),
      new Skill('Science', 'mental'),
      new Skill('Athletics', 'physical'),
      new Skill('Brawl', 'physical'),
      new Skill('Drive', 'physical'),
      new Skill('Firearms', 'physical'),
      new Skill('Larceny', 'physical'),
      new Skill('Stealth', 'physical'),
      new Skill('Survival', 'physical'),
      new Skill('Weaponry', 'physical'),
      new Skill('Animal Ken', 'social'),
      new Skill('Empathy', 'social'),
      new Skill('Expression', 'social'),
      new Skill('Intimidation', 'social'),
      new Skill('Persuasion', 'social'),
      new Skill('Socialize', 'social'),
      new Skill('Streetwise', 'social'),
      new Skill('Subterfuge', 'social')
    ];
  }

  /**
   * Returns an array of core Merits and their prerequisites.
   *
   * @return {Merit[]} The array of core Merits and their prerequisites.
   */
  function getCoreMerits() {
    return [
      new Merit('Area of Expertise', 'mental', [
        [
          new AttributePrerequisite('resolve', 2),
          new SkillSpecialtyPrerequisite('any', 1)
        ]
      ]),
      new Merit('Common Sense', 'mental'),
      new Merit('Danger Sense', 'mental'),
      new Merit('Direction Sense', 'mental'),
      new Merit('Eidetic Memory', 'mental'),
      new Merit('Encyclopedic Knowledge', 'mental'),
      new Merit('Eye for the Strange', 'mental', [
        [
          new AttributePrerequisite('resolve', 2),
          new SkillPrerequisite('occult', 1)
        ]
      ]),
      new Merit('Fast Reflexes', 'mental', [
        [new AttributePrerequisite('wits', 3)],
        [new AttributePrerequisite('dexterity', 3)]
      ]),
      new Merit('Good Time Management', 'mental', [
        [new SkillPrerequisite('academics', 2)],
        [new SkillPrerequisite('science', 2)]
      ]),
      new Merit('Holistic Awareness', 'mental'),
      new Merit('Indomitable', 'mental', [
        [new AttributePrerequisite('resolve', 3)]
      ]),
      new Merit('Interdisciplinary Specialty', 'mental', [
        [new SkillSpecialtyPrerequisite('any', 3)]
      ]),
      new Merit('Investigating Prodigy', 'mental', [
        [
          new AttributePrerequisite('wits', 3),
          new SkillPrerequisite('investigation', 3)
        ]
      ]),
      new Merit('Language', 'mental'),
      new Merit('Library', 'mental'),
      new Merit('Meditative Mind', 'mental'),
      new Merit('Multilingual', 'mental'),
      new Merit('Patient', 'mental'),
      new Merit('Professional Training', 'mental'),
      new Merit('Tolerance for Biology', 'mental', [
        [new AttributePrerequisite('resolve', 3)]
      ]),
      new Merit('Trained Observer', 'mental', [
        [new AttributePrerequisite('wits', 3)],
        [new AttributePrerequisite('composure', 3)]
      ]),
      new Merit('Vice-Ridden', 'mental'),
      new Merit('Virtuous', 'mental'),
      new Merit('Ambidextrous', 'physical'),
      new Merit('Automotive Genius', 'physical', [
        [
          new SkillPrerequisite('crafts', 3),
          new SkillPrerequisite('drive', 1),
          new SkillPrerequisite('science', 1)
        ]
      ]),
      new Merit('Crack Driver', 'physical', [
        [new SkillPrerequisite('drive', 3)]
      ]),
      new Merit('Demolisher', 'physical', [
        [new AttributePrerequisite('strength', 3)],
        [new AttributePrerequisite('intelligence', 3)]
      ]),
      new Merit('Double Jointed', 'physical', [
        [new AttributePrerequisite('dexterity', 3)]
      ]),
      new Merit('Fleet of Foot', 'physical', [
        [new SkillPrerequisite('athletics', 2)]
      ]),
      new Merit('Giant', 'physical'),
      new Merit('Hardy', 'physical', [
        [new AttributePrerequisite('stamina', 3)]
      ]),
      new Merit('Greyhound', 'physical', [
        [
          new SkillPrerequisite('athletics', 3),
          new AttributePrerequisite('wits', 3),
          new AttributePrerequisite('stamina', 3)
        ]
      ]),
      new Merit('Iron Stamina', 'physical', [
        [new AttributePrerequisite('stamina', 3)],
        [new AttributePrerequisite('resolve', 3)]
      ]),
      new Merit('Parkour', 'physical', [
        [
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('athletics', 2)
        ]
      ]),
      new Merit('Quick Draw', 'physical', [
        [
          new AttributePrerequisite('wits', 3),
          new SkillSpecialtyPrerequisite('firearms', 1)
        ],
        [
          new AttributePrerequisite('wits', 3),
          new SkillSpecialtyPrerequisite('weaponry', 1)
        ]
      ]),
      new Merit('Relentless', 'physical', [
        [
          new SkillPrerequisite('athletics', 2),
          new AttributePrerequisite('stamina', 3)
        ]
      ]),
      new Merit('Seizing the Edge', 'physical', [
        [
          new AttributePrerequisite('wits', 3),
          new AttributePrerequisite('composure', 3)
        ]
      ]),
      new Merit('Sleight of Hand', 'physical', [
        [new SkillPrerequisite('larceny', 3)]
      ]),
      new Merit('Small-Framed', 'physical'),
      new Merit('Stunt Driver', 'physical', [
        [
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('drive', 3),
          new AttributePrerequisite('wits', 3)
        ]
      ]),
      new Merit('Allies', 'social'),
      new Merit('Alternate Identity', 'social'),
      new Merit('Anonymity', 'social', [
        [new MeritPrerequisite('Fame', false)]
      ]),
      new Merit('Barfly', 'social', [
        [new SkillPrerequisite('socialize', 2)]
      ]),
      new Merit('Closed Book', 'social', [
        [
          new AttributePrerequisite('manipulation', 3),
          new AttributePrerequisite('resolve', 3)
        ]
      ]),
      new Merit('Contacts', 'social'),
      new Merit('Fame', 'social', [
        [new MeritPrerequisite('Anonymity', false)]
      ]),
      new Merit('Fast-Talking', 'social', [
        [
          new AttributePrerequisite('manipulation', 3),
          new SkillPrerequisite('subterfuge', 2)
        ]
      ]),
      new Merit('Fixer', 'social', [
        [
          new AttributePrerequisite('wits', 3),
          new MeritPrerequisite('Contacts', true)
        ]
      ]),
      new Merit('Hobbyist Clique', 'social', [
        [new SkillPrerequisite('any', 2)]
      ]),
      new Merit('Inspiring', 'social', [
        [new AttributePrerequisite('presence', 3)]
      ]),
      new Merit('Iron Will', 'social', [
        [new AttributePrerequisite('resolve', 4)]
      ]),
      new Merit('Mentor', 'social'),
      new Merit('Mystery Cult Initiation', 'social'),
      new Merit('Pusher', 'social', [
        [new AttributePrerequisite('persuasion', 2)]
      ]),
      new Merit('Resources', 'social'),
      new Merit('Retainer', 'social'),
      new Merit('Safe Place', 'social'),
      new Merit('Small Unit Tactics', 'social', [
        [new AttributePrerequisite('presence', 3)]
      ]),
      new Merit('Spin Doctor', 'social', [
        [
          new AttributePrerequisite('manipulation', 3),
          new SkillPrerequisite('subterfuge', 2)
        ]
      ]),
      new Merit('Staff', 'social'),
      new Merit('Status', 'social'),
      new Merit('Striking Looks', 'social'),
      new Merit('Sympathetic', 'social'),
      new Merit('Table Turner', 'social', [
        [
          new AttributePrerequisite('composure', 3),
          new AttributePrerequisite('manipulation', 3),
          new AttributePrerequisite('wits', 3)
        ]
      ]),
      new Merit('Takes One to Know One', 'social'),
      new Merit('Taste', 'social', [
        [
          new SkillPrerequisite('crafts', 2),
          new SkillSpecialtyPrerequisite('crafts', 1)
        ],
        [
          new SkillPrerequisite('crafts', 2),
          new SkillSpecialtyPrerequisite('expression', 1)
        ]
      ]),
      new Merit('True Friend', 'social'),
      new Merit('Untouchable', 'social', [
        [
          new AttributePrerequisite('manipulation', 3),
          new SkillPrerequisite('subterfuge', 2)
        ]
      ]),
      new Merit('Armed Defense', 'fighting', [
        [
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('weaponry', 2),
          new MeritPrerequisite('Defensive Combat', true)
        ]
      ]),
      new Merit('Cheap Shot', 'fighting', [
        [
          new SkillPrerequisite('subterfuge', 2),
          new MeritPrerequisite('Street Fighting', true)
        ]
      ]),
      new Merit('Choke Hold', 'fighting', [
        [new SkillPrerequisite('brawl', 2)]
      ]),
      new Merit('Close Quarters Combat', 'fighting', [
        [
          new AttributePrerequisite('wits', 3),
          new SkillPrerequisite('athletics', 2),
          new SkillPrerequisite('brawl', 3)
        ]
      ]),
      new Merit('Defensive Combat', 'fighting', [
        [new SkillPrerequisite('brawl', 1)],
        [new SkillPrerequisite('weaponry', 1)]
      ]),
      new Merit('Fighting Finesse', 'fighting', [
        [
          new AttributePrerequisite('dexterity', 3),
          new SkillSpecialtyPrerequisite('brawl', 1)
        ],
        [
          new AttributePrerequisite('dexterity', 3),
          new SkillSpecialtyPrerequisite('weaponry', 1)
        ]
      ]),
      new Merit('Firefight', 'fighting', [
        [
          new AttributePrerequisite('composure', 3),
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('athletics', 2),
          new SkillPrerequisite('firearms', 2)
        ]
      ]),
      new Merit('Grappling', 'fighting', [
        [
          new AttributePrerequisite('stamina', 3),
          new AttributePrerequisite('strength', 2),
          new SkillPrerequisite('athletics', 2),
          new SkillPrerequisite('brawl', 2)
        ]
      ]),
      new Merit('Heavy Weapons', 'fighting', [
        [
          new AttributePrerequisite('stamina', 3),
          new AttributePrerequisite('strength', 3),
          new SkillPrerequisite('athletics', 2),
          new SkillPrerequisite('weaponry', 2)
        ]
      ]),
      new Merit('Improvised Weaponry', 'fighting', [
        [
          new AttributePrerequisite('wits', 3),
          new SkillPrerequisite('weaponry', 1)
        ]
      ]),
      new Merit('Iron Skin', 'fighting', [
        [
          new AttributePrerequisite('stamina', 3),
          new MeritPrerequisite('Martial Arts', true)
        ],
        [
          new AttributePrerequisite('stamina', 3),
          new MeritPrerequisite('Street Fighting', true)
        ]
      ]),
      new Merit('Light Weapons', 'fighting', [
        [
          new AttributePrerequisite('wits', 3),
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('athletics', 2),
          new SkillPrerequisite('weaponry', 2),
          new MeritPrerequisite('Fighting Finesse', true)
        ]
      ]),
      new Merit('Marksmanship', 'fighting', [
        [
          new AttributePrerequisite('composure', 3),
          new AttributePrerequisite('resolve', 3),
          new SkillPrerequisite('firearms', 2)
        ]
      ]),
      new Merit('Martial Arts', 'fighting', [
        [
          new AttributePrerequisite('resolve', 3),
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('athletics', 2),
          new SkillPrerequisite('brawl', 2)
        ]
      ]),
      new Merit('Policed Tactics', 'fighting', [
        [
          new SkillPrerequisite('brawl', 2),
          new SkillPrerequisite('weaponry', 2)
        ]
      ]),
      new Merit('Shiv', 'fighting', [
        [
          new SkillPrerequisite('weaponry', 1),
          new MeritPrerequisite('Street Fighting', true)
        ]
      ]),
      new Merit('Street Fighting', 'fighting', [
        [
          new AttributePrerequisite('stamina', 3),
          new AttributePrerequisite('composure', 3),
          new SkillPrerequisite('brawl', 2),
          new SkillPrerequisite('streetwise', 2)
        ]
      ]),
      new Merit('Unarmed Defense', 'fighting', [
        [
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('brawl', 2),
          new MeritPrerequisite('Defensive Combat', true)
        ]
      ])
    ];
  }

  /**
   * Returns an array of Merits specific to Mortals and their prerequisites.
   *
   * @return {Merit[]} The array of Merits specific to Mortals and their prerequisites.
   */
  function getMortalMerits() {
    return [
      new Merit('Aura Reading', 'supernatural'),
      new Merit('Automatic Writing', 'supernatural'),
      new Merit('Biokinesis', 'supernatural'),
      new Merit('Clairvoyance', 'supernatural'),
      new Merit('Cursed', 'supernatural'),
      new Merit('Laying on Hands', 'supernatural'),
      new Merit('Medium', 'supernatural', [
        [new SkillPrerequisite('empathy', 2)]
      ]),
      new Merit('Mind of a Madman', 'supernatural', [
        [new SkillPrerequisite('empathy', 3)]
      ]),
      new Merit('Omen Sensitivity', 'supernatural'),
      new Merit('Numbing Touch', 'supernatural'),
      new Merit('Psychokinesis', 'supernatural'),
      new Merit('Psychometry', 'supernatural'),
      new Merit('Telekinesis', 'supernatural'),
      new Merit('Telepathy', 'supernatural'),
      new Merit('Thief of Fate', 'supernatural'),
      new Merit('Unseen Sense', 'supernatural')
    ];
  }

  /**
   * Returns an array of Merits specific to Vampires and their prerequisites.
   *
   * @return {Merit[]} The array of Merits specific to Vampires and their prerequisites.
   */
  function getVampireMerits() {
    return [];
  }

  /**
   * Returns an array of Merits specific to Werewolves and their prerequisites.
   *
   * @return {Merit[]} The array of Merits specific to Werewolves and their prerequisites.
   */
  function getWerewolfMerits() {
    return [
      new Merit('Anchored', 'supernatural'),
      // new Merit('Blood or Bone Affinity', 'supernatural'),
      // new Merit('Code of Honor', 'supernatural'),
      new Merit('Controlled Burn', 'supernatural', [
        [
          new AttributePrerequisite('resolve', 3),
          new AttributePrerequisite('composure', 3)
        ]
      ]),
      // new Merit('Creative Tactician', 'supernatural'),
      new Merit('Dedicated Locus', 'supernatural', [
        [new MeritPrerequisite('Safe Place', true)]
      ]),
      // new Merit('Embodiment of the Firstborn', 'supernatural'),
      // new Merit('Fading', 'supernatural'),
      // new Merit('Favored Form', 'supernatural'),
      new Merit('Fortified Form', 'supernatural'[
        [
          new AttributePrerequisite('stamina', 3),
          new SkillPrerequisite('survival', 2)
        ]
        ]),
      // new Merit('Hearing Whispers', 'supernatural'),
      // new Merit('Impartial Mediator', 'supernatural'),
      new Merit('Living Weapon', 'supernatural', [
        [
          new AttributePrerequisite('stamina', 3),
          new SkillPrerequisite('survival', 2)
        ]
      ]),
      // new Merit('Moon-Kissed', 'supernatural'),
      // new Merit('Nowhere to Run', 'supernatural'),
      new Merit('Pack Dynamics', 'supernatural'),
      new Merit('Residential Area', 'supernatural'),
      // new Merit('Resonance Shaper', 'supernatural'),
      new Merit('Self-Control', 'supernatural', [
        [new AttributePrerequisite('resolve', 4)]
      ]),
      // new Merit('Song in your Heart', 'supernatural'),
      // new Merit('Sounds of the City', 'supernatural'),
      // new Merit('Strings of the Heart','supernatural'),
      new Merit('Totem', 'supernatural'),
      // new Merit('Weakest Link','supernatural'),
      // new Merit('Call Out','fighting',[
      //   [
      //     new SkillPrerequisite('intimidation',2),
      //     new AttributePrerequisite('composure',3)
      //   ]
      // ]),
      // new Merit('Efficient Killer','fighting',[
      //   [
      //     new SkillPrerequisite('brawl',3),
      //     new SkillPrerequisite('medicine',2),
      //     new AttributePrerequisite('strength',3)
      //   ]
      // ]),
      // new Merit('Flanking','fighting',[
      //   [
      //     new AttributePrerequisite('wits',3),
      //     new SkillPrerequisite('stealth',2),
      //     new SkillPrerequisite('brawl',2)
      //   ]
      // ]),
      // new Merit('Instinctive Defense','fighting',[
      //   [
      //     new SkillPrerequisite('athletics',2)
      //   ]
      // ]),
      new Merit('Relentless Assault', 'fighting', [
        [
          new AttributePrerequisite('strength', 3),
          new AttributePrerequisite('stamina', 3),
          new SkillPrerequisite('brawl', 2)
        ]
      ]),
      new Merit('Spiritual Blockage', 'fighting', [
        [
          new SkillPrerequisite('brawl', 1),
          new SkillPrerequisite('occult', 3),
          new AttributePrerequisite('wits', 3)
        ]
      ]),
      new Merit('Tactical Shifting', 'fighting', [
        [
          new AttributePrerequisite('wits', 3),
          new AttributePrerequisite('dexterity', 3),
          new SkillPrerequisite('athletics', 2)
        ]
      ])
      // new Merit('Warcry', 'fighting',[
      //   [
      //     new AttributePrerequisite('presence',3),
      //     new SkillPrerequisite('expression',2),
      //     new SkillPrerequisite('intimidation',2)
      //   ]
      // ])
    ];
  }

  /**
   * Returns an array of Merits specific to Mages and their prerequisites.
   *
   * @return {Merit[]} The array of Merits specific to Mages and their prerequisites.
   */
  function getMageMerits() {
    return [];
  }

  // endregion
  // endregion

  // region Execute
  initialize();
  // endregion
})(componentHandler);
